import { useState, useEffect } from 'react';
import { Sparkles, AlertTriangle, CheckCircle, Flame, Lightbulb, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { api, getCurrentMonth } from '../../lib/api';

const ICON_MAP = {
  success: CheckCircle,
  warning: AlertTriangle,
  danger: Flame,
  tip: Lightbulb,
};

const COLOR_MAP = {
  success: 'rgb(var(--lm-green))',
  warning: 'rgb(var(--lm-orange))',
  danger: 'rgb(var(--lm-red))',
  tip: 'rgb(var(--lm-gold))',
};

const BG_MAP = {
  success: 'rgb(var(--lm-green) / 0.06)',
  warning: 'rgb(var(--lm-orange) / 0.06)',
  danger: 'rgb(var(--lm-red) / 0.06)',
  tip: 'rgb(var(--lm-gold) / 0.06)',
};

// Client-side cache: avoids re-calling OpenAI every time user navigates between pages
// Keyed by `${page}|${role}|${mois}` with 15 min TTL
const INSIGHTS_TTL_MS = 15 * 60 * 1000;
const _insightsCache = new Map();

export const AIInsights = ({ page = 'dashboard', role = 'patron' }) => {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const [loaded, setLoaded] = useState(false);

  const accentColor = role === 'patron' ? 'rgb(var(--lm-gold))' : 'rgb(var(--lm-green))';

  const fetchInsights = async (force = false) => {
    const mois = getCurrentMonth();
    const cacheKey = `${page}|${role}|${mois}`;

    // Serve from client cache if fresh and not forced
    if (!force) {
      const cached = _insightsCache.get(cacheKey);
      if (cached && cached.expires > Date.now()) {
        setInsights(cached.data || []);
        setLoaded(true);
        return;
      }
    }

    setLoading(true);
    try {
      const res = await api.get(`/ai-advisor/insights/${page}`, {
        params: { role, mois },
        timeout: 8000, // hard client-side timeout to avoid UI hang
      });
      const data = res.data.insights && res.data.insights.length > 0 ? res.data.insights : [];
      setInsights(data);
      _insightsCache.set(cacheKey, { data, expires: Date.now() + INSIGHTS_TTL_MS });
    } catch {
      // Silent fail — insights are optional
      _insightsCache.set(cacheKey, { data: [], expires: Date.now() + 2 * 60 * 1000 });
    }
    setLoading(false);
    setLoaded(true);
  };

  useEffect(() => {
    setLoaded(false);
    setInsights([]);
    fetchInsights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, role]);

  if (!loaded || (loaded && insights.length === 0 && !loading)) return null;

  return (
    <div className="mb-4 animate-fade-in" data-testid="ai-insights">
      <div
        className="rounded-xl overflow-hidden transition-all"
        style={{ border: `1px solid ${accentColor}15`, background: `${accentColor}04` }}
      >
        {/* Header */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-between px-3.5 py-2 transition-colors hover:bg-[rgb(var(--lm-bg)/0.3)]"
          data-testid="ai-insights-toggle"
        >
          <div className="flex items-center gap-2">
            <Sparkles size={14} style={{ color: accentColor }} />
            <span className="text-[10px] tracking-[0.2em] uppercase font-medium" style={{ color: accentColor }}>
              Conseils Max
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            {!loading && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); fetchInsights(true); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); fetchInsights(true); } }}
                className="p-1 rounded hover:bg-[rgb(var(--lm-bg)/0.5)] transition-colors cursor-pointer"
                data-testid="ai-insights-refresh"
              >
                <RefreshCw size={12} className="text-[rgb(var(--lm-text-muted))]" />
              </span>
            )}
            {collapsed ? (
              <ChevronDown size={14} className="text-[rgb(var(--lm-text-muted))]" />
            ) : (
              <ChevronUp size={14} className="text-[rgb(var(--lm-text-muted))]" />
            )}
          </div>
        </button>

        {/* Content */}
        {!collapsed && (
          <div className="px-3.5 pb-3 space-y-1.5">
            {loading ? (
              <div className="flex items-center gap-2 py-2">
                <div className="w-3 h-3 rounded-full animate-pulse" style={{ background: accentColor }} />
                <span className="text-xs text-[rgb(var(--lm-text-muted))]">Max analyse vos données...</span>
              </div>
            ) : (
              insights.map((insight, idx) => {
                const Icon = ICON_MAP[insight.type] || Lightbulb;
                const color = COLOR_MAP[insight.type] || accentColor;
                const bg = BG_MAP[insight.type] || `${accentColor}08`;
                return (
                  <div
                    key={idx}
                    className="flex items-start gap-2 px-3 py-2 rounded-lg text-xs"
                    style={{ background: bg }}
                    data-testid={`ai-insight-${idx}`}
                  >
                    <Icon size={14} className="shrink-0 mt-0.5" style={{ color }} />
                    <span style={{ color: 'rgb(var(--lm-text-secondary))' }}>{insight.text}</span>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};
